/**
 * Environment Mismatch Guard
 *
 * Runtime check that warns if the app is running on a production domain
 * with non-production Firebase config, or vice versa.
 *
 * This catches misconfigurations like:
 * - Deploying a staging build to the production domain
 * - Running prod config on localhost (less critical but worth logging)
 *
 * Call once at app startup (e.g., in main.tsx).
 */

const env = import.meta.env;

/** Known production hostnames for the app */
const PRODUCTION_HOSTS = ['app.modcar.io', 'modcar-final.web.app', 'modcar-final.firebaseapp.com'];

/** Known local/dev hostnames */
const LOCAL_HOSTS = ['localhost', '127.0.0.1'];

/**
 * Check if the current hostname is a known production host.
 */
function isProductionHost(): boolean {
  if (typeof window === 'undefined') return false;
  return PRODUCTION_HOSTS.includes(window.location.hostname);
}

/**
 * Check if the current hostname is localhost/dev.
 */
function isLocalHost(): boolean {
  if (typeof window === 'undefined') return false;
  return LOCAL_HOSTS.includes(window.location.hostname);
}

/**
 * Run environment mismatch checks and log warnings.
 *
 * Returns an object describing the environment state for diagnostics.
 */
export function checkEnvMismatch(): {
  hostname: string;
  projectId: string;
  appEnv: string;
  isProduction: boolean;
  issues: string[];
} {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : 'unknown';
  const projectId = env.VITE_FIREBASE_PROJECT_ID || 'unknown';
  const appEnv = env.VITE_APP_ENV || 'development';
  const isProduction = isProductionHost();
  const issues: string[] = [];

  // Check 1: Production host with non-production env label
  if (isProduction && appEnv !== 'production') {
    issues.push(
      `[EnvGuard] ⚠️ Running on production host (${hostname}) but VITE_APP_ENV="${appEnv}". ` +
        `Expected "production". Sentry, analytics, and error reporting may not work correctly.`
    );
  }

  // Check 2: Production host with wrong project ID
  if (isProduction && projectId !== 'modcar-final') {
    issues.push(
      `[EnvGuard] ⚠️ Running on production host (${hostname}) but VITE_FIREBASE_PROJECT_ID="${projectId}". ` +
        `Expected "modcar-final". Data will go to the wrong Firestore database!`
    );
  }

  // Check 3: Local host with production env label (less critical, just informational)
  if (isLocalHost() && appEnv === 'production') {
    issues.push(
      `[EnvGuard] ℹ️ Running on localhost with VITE_APP_ENV="production". ` +
        `This is unusual — make sure this is intentional.`
    );
  }

  // Check 4: authDomain should match the project
  const authDomain = env.VITE_FIREBASE_AUTH_DOMAIN || '';
  if (isProduction && authDomain && !authDomain.includes('modcar-final')) {
    issues.push(
      `[EnvGuard] ⚠️ Running on production host but VITE_FIREBASE_AUTH_DOMAIN="${authDomain}" ` +
        `doesn't reference the production project. OAuth redirects may fail!`
    );
  }

  // Log all issues
  for (const issue of issues) {
    // Use logger instead of console for production safety
    if (typeof window !== 'undefined') {
      import('@/lib/logger').then(({ logger }) => logger.warn(issue));
    }
  }

  // In production with issues, also log a summary to help debugging
  if (isProduction && issues.length > 0) {
    const errorMsg =
      `[EnvGuard] ${issues.length} environment mismatch(es) detected on ${hostname}. ` +
      `This may cause auth failures, data loss, or broken features.`;
    if (typeof window !== 'undefined') {
      import('@/lib/logger').then(({ logger }) => logger.error(errorMsg));
    }
  }

  return { hostname, projectId, appEnv, isProduction, issues };
}
